import { Box, createStyles, CSSObject, MantineTheme, useMantineTheme } from "@mantine/core";
import { useMergedRef } from "@mantine/hooks";
import React from "react";
import { IconFn, IconV2, RpiIconButton } from "../..";
import { Flex } from "../RpiFlex";

interface UseRpiInputStylesProps {
  isError?: boolean;
  hasIcon?: boolean;
  is?: Partial<Record<"textarea", boolean>>;
}

type RpiInputStyles = "root" | "label" | "wrapper" | "input" | "error" | "action" | "icon";

const useRpiInputStyles = createStyles<RpiInputStyles, UseRpiInputStylesProps>(
  (theme, { isError, hasIcon, is = {} }) => {
    return {
      root: {},
      label: {
        ...theme.other.fontTypes.p3,
        fontWeight: theme.other.fontWeights.regular,
        color: theme.colors.brand[5],
        marginLeft: 26,
      },
      wrapper: {
        position: "relative",
        borderRadius: 32,
        width: "100%",
        ...(is.textarea ? { height: "fit-content", minHeight: 52 } : { height: 52 }),
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: isError ? theme.colors.spark[6] : theme.colors.calm[5],
        backgroundColor: theme.colors.sky[5],

        "&:focus-within": {
          borderColor: isError ? theme.colors.spark[6] : theme.colors.amethyst[5],
        },
      },
      input: {
        ...theme.other.fontTypes.p3,
        fontWeight: theme.other.fontWeights.regular,
        color: theme.colors.brand[5],
        outline: "none",
        background: "transparent",
        border: "none",
        ...(is.textarea
          ? { margin: "14px 24px", padding: 0, resize: "none", height: "auto" }
          : { padding: "16px 24px", margin: 0, height: "100%" }),
        width: "100%",

        "&:-webkit-autofill, &:-webkit-autofill:focus": {
          // transition: "background-color 600000s 0s, color 600000s 0s",
          borderRadius: "inherit",
        },
        "&::placeholder": {
          color: theme.colors.brand[5],
          fontWeight: theme.other.fontWeights.light,
        },
        ...(hasIcon && {
          paddingLeft: 8,
        }),
      },
      error: {
        ...theme.other.fontTypes.label,
        fontWeight: theme.other.fontWeights.regular,
        color: theme.colors.spark[6],
        marginLeft: 26,
        marginRight: 26,
      },
      action: {
        color: theme.colors.brand[5],
        paddingRight: 24,
        flexShrink: 0,
        "& svg": {
          color: theme.colors.brand[5],
        },
      },
      icon: {
        paddingLeft: 24,
      },
    };
  }
);

export interface RpiInputProps {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  classNames?: Partial<Record<RpiInputStyles, string>>;
  styles?:
    | Partial<Record<RpiInputStyles, CSSObject>>
    | ((theme: MantineTheme) => Partial<Record<RpiInputStyles, CSSObject>>);
  icon?: IconFn;
  action?: React.ReactNode;
}

type RpiTextInputProps = RpiInputProps &
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const RpiTextInput = React.forwardRef<HTMLInputElement, RpiTextInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,
      icon,
      action,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiInputStyles({ isError: !!error, hasIcon: !!icon });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLInputElement>, ref);

    return (
      <Flex.Column
        gap={8}
        fullWidth={fullWidth}
        className={cx(classes.root, classNames?.root)}
        sx={styles.root}
        onClick={() => inputRef.current?.focus()}
      >
        {label && (
          <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label} children={label} />
        )}

        <Flex.Row className={cx(classes.wrapper, classNames?.wrapper)} sx={styles.wrapper} align="center">
          {icon && (
            <Box
              className={cx(classes.icon, classNames?.icon)}
              sx={styles.icon}
              children={React.createElement(icon, { size: 18 })}
            />
          )}
          <Box
            {...inputProps}
            component="input"
            ref={refs as any}
            type="text"
            className={cx(classes.input, classNames?.input)}
            sx={styles.input}
          />
          {action && (
            <Flex.Row
              align="center"
              className={cx(classes.action, classNames?.action)}
              sx={styles.action}
              children={action}
            />
          )}
        </Flex.Row>

        {error && <Box className={cx(classes.error, classNames?.error)} sx={styles?.error} children={error} />}
      </Flex.Column>
    );
  }
);

type RpiPasswordInputProps = RpiInputProps &
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const RpiPasswordInput = React.forwardRef<HTMLInputElement, RpiPasswordInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,
      icon,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiInputStyles({ isError: !!error, hasIcon: !!icon });

    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const [capsLock, setCapsLock] = React.useState(false);

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLInputElement>, ref);

    return (
      <Flex.Column
        gap={8}
        fullWidth={fullWidth}
        className={cx(classes.root, classNames?.root)}
        sx={styles.root}
        onClick={() => inputRef.current?.focus()}
      >
        {label && (
          <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label} children={label} />
        )}

        <Flex.Row className={cx(classes.wrapper, classNames?.wrapper)} sx={styles.wrapper} align="center">
          {icon && (
            <Box
              className={cx(classes.icon, classNames?.icon)}
              sx={styles.icon}
              children={React.createElement(icon, { size: 18 })}
            />
          )}
          <Box
            {...inputProps}
            component="input"
            ref={refs as any}
            type={showPassword ? "text" : "password"}
            className={cx(classes.input, classNames?.input)}
            sx={styles.input}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              inputProps.onKeyDown && inputProps.onKeyDown(e);
              setCapsLock(!!e.getModifierState("CapsLock"));
            }}
          />
          <Flex.Row align="center" className={cx(classes.action, classNames?.action)} sx={styles?.action}>
            <RpiIconButton
              icon={showPassword ? IconV2.Eye : IconV2.EyeOff}
              size={16}
              color="currentColor"
              onClick={() => setShowPassword((showPassword) => !showPassword)}
            />
          </Flex.Row>
        </Flex.Row>

        {(error || capsLock) && (
          <Box
            className={cx(classes.error, classNames?.error)}
            sx={styles?.error}
            children={
              error ? (
                error
              ) : (
                <Flex.Row align="center" gap={4}>
                  <IconV2.ErrorSolid size={14} color="currentColor" />
                  Caps Lock is on
                </Flex.Row>
              )
            }
          />
        )}
      </Flex.Column>
    );
  }
);

type RpiMultilineInputProps = RpiInputProps &
  React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

export const RpiMultilineInput = React.forwardRef<HTMLTextAreaElement, RpiMultilineInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,
      icon,
      action,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiInputStyles({ isError: !!error, hasIcon: !!icon, is: { textarea: true } });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLTextAreaElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLTextAreaElement>, ref);

    return (
      <Flex.Column
        gap={8}
        fullWidth={fullWidth}
        className={cx(classes.root, classNames?.root)}
        sx={styles.root}
        onClick={() => inputRef.current?.focus()}
      >
        {label && (
          <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label} children={label} />
        )}

        <Flex.Row className={cx(classes.wrapper, classNames?.wrapper)} sx={styles.wrapper} align="center">
          {icon && (
            <Box
              className={cx(classes.icon, classNames?.icon)}
              sx={styles.icon}
              children={React.createElement(icon, { size: 18 })}
            />
          )}
          <Box
            rows={1}
            {...inputProps}
            component="textarea"
            ref={refs as any}
            className={cx(classes.input, classNames?.input)}
            sx={styles.input}
          />
          {action && (
            <Flex.Row
              align="center"
              className={cx(classes.action, classNames?.action)}
              sx={styles.action}
              children={action}
            />
          )}
        </Flex.Row>

        {error && <Box className={cx(classes.error, classNames?.error)} sx={styles?.error} children={error} />}
      </Flex.Column>
    );
  }
);
